import type { ProjectMembership } from "@medplum/fhirtypes";
import { MEMBERSHIP_PRACTICE_NAME_SYSTEM } from "@/lib/auth/membership-practice-identifier";
import type { ProfileResource } from "@/lib/auth/profile-types";

export interface ProfileOption {
  membership: ProjectMembership;
  displayName: string;
  typeLabel: string;
  practiceNames: string[];
  practiceLabel: string;
  identifierLabel?: string;
}

/**
 * Builds display-ready profile options from project memberships.
 *
 * During profile selection the Medplum client has no active session — startLogin
 * returned memberships but no code has been processed yet. Any authenticated API
 * call (readReference, search, etc.) would receive a 401, triggering the SDK's
 * handleUnauthenticated → clear() → sessionStorage.clear(). That wipes the PKCE
 * codeVerifier stored by startLogin, causing the subsequent processCode token
 * exchange to fail with "Failed to fetch tokens".
 *
 * We therefore build options entirely from the membership metadata already in
 * memory — display names, identifiers, and practice names embedded at invite time.
 */
export function buildProfileOptions({
  memberships,
}: {
  medplum: unknown;
  memberships: ProjectMembership[];
}): ProfileOption[] {
  return memberships.map((membership) => buildProfileOption({ membership }));
}

/**
 * Builds a single profile option from membership metadata.
 */
function buildProfileOption({ membership }: { membership: ProjectMembership }): ProfileOption {
  const profileType = getProfileTypeFromReference(membership.profile?.reference);
  const identifierLabel = getMembershipIdentifierLabel(membership);
  const practiceNameFromIdentifier = getMembershipPracticeName(membership);

  return {
    membership,
    displayName: getDisplayName({ membership, profileType }),
    typeLabel: getProfileTypeLabel(profileType),
    practiceNames: practiceNameFromIdentifier ? [practiceNameFromIdentifier] : [],
    practiceLabel: practiceNameFromIdentifier
      ? formatPracticeLabel([practiceNameFromIdentifier])
      : "",
    identifierLabel,
  };
}

/**
 * Resolves a readable display name from the membership reference or identifiers.
 */
function getDisplayName({
  membership,
  profileType,
}: {
  membership: ProjectMembership;
  profileType?: ProfileResource["resourceType"];
}): string {
  // HumanName from the reference's display field (Medplum populates this)
  const profileDisplay = membership.profile?.display?.trim();
  if (profileDisplay) {
    return profileDisplay;
  }

  const identifierLabel = getMembershipIdentifierLabel(membership);
  if (identifierLabel) {
    return identifierLabel;
  }

  return getProfileTypeLabel(profileType);
}

/**
 * Returns the profile type from a profile reference string.
 */
function getProfileTypeFromReference(
  profileReference: string | undefined,
): ProfileResource["resourceType"] | undefined {
  if (!profileReference) {
    return undefined;
  }

  const [resourceType] = profileReference.split("/");
  if (
    resourceType === "Patient" ||
    resourceType === "Practitioner" ||
    resourceType === "RelatedPerson"
  ) {
    return resourceType;
  }

  return undefined;
}

/**
 * Returns a user-facing label for the profile type.
 */
function getProfileTypeLabel(profileType: ProfileResource["resourceType"] | undefined): string {
  if (profileType === "Practitioner") {
    return "Provider";
  }
  if (profileType === "Patient") {
    return "Patient";
  }
  if (profileType === "RelatedPerson") {
    return "Caregiver";
  }
  return "Profile";
}

/**
 * Returns a short practice label suitable for UI display.
 */
function formatPracticeLabel(practiceNames: string[]): string {
  if (!practiceNames.length) {
    return "Practice not available";
  }

  if (practiceNames.length === 1) {
    return practiceNames[0];
  }

  if (practiceNames.length <= 3) {
    return practiceNames.join(", ");
  }

  return `${practiceNames[0]} + ${practiceNames.length - 1} more`;
}

/**
 * Returns a label from membership identifiers when available.
 * Skips the practice-name identifier since that's used for the practice label.
 */
function getMembershipIdentifierLabel(membership: ProjectMembership): string | undefined {
  const identifier = membership.identifier?.find(
    (id) => id.system !== MEMBERSHIP_PRACTICE_NAME_SYSTEM,
  );
  return identifier?.value?.trim() || undefined;
}

/**
 * Reads the practice name embedded on the membership at invite time.
 */
function getMembershipPracticeName(membership: ProjectMembership): string | undefined {
  const practiceIdentifier = membership.identifier?.find(
    (id) => id.system === MEMBERSHIP_PRACTICE_NAME_SYSTEM,
  );
  return practiceIdentifier?.value?.trim() || undefined;
}
